import React, { useEffect, useRef, useState } from 'react';

import { buscarMencionaveis } from '../../api/usersApi';
import { notify } from '../../shared/feedback/notify';

/** Uma pessoa escolhida no autocomplete. */
export interface MencaoEscolhida {
  id: string;
  username: string;
}

interface Sugestao {
  id: string;
  fullName: string | null;
  username: string | null;
}

interface Props {
  value: string;
  onChange: (texto: string) => void;
  /** As pessoas escolhidas até aqui. Quem envia usa `idsMencionadosNoTexto` para filtrá-las. */
  mencoes: MencaoEscolhida[];
  onMencoesChange: (mencoes: MencaoEscolhida[]) => void;
  placeholder?: string;
  ariaLabel: string;
}

/**
 * O `@trecho` que está sendo digitado logo antes do cursor — inclusive VAZIO, logo depois do @: a
 * lista abre já no @ e filtra a cada letra (Victor, 23/10/2026). Nulo quando não há @ ativo.
 */
// Classe explícita, sem `\p{L}`: propriedade Unicode em regex depende do alvo do Babel do CRA.
const TOKEN = /(^|\s)@([A-Za-zÀ-ÿ0-9._-]*)$/;

/**
 * Os ids que valem no envio: só os das pessoas cujo `@username` AINDA está no texto. Quem apagou a
 * menção depois de escolher não pode ter a pessoa avisada mesmo assim.
 */
export function idsMencionadosNoTexto(texto: string, mencoes: MencaoEscolhida[]): string[] {
  const ids = mencoes
    .filter((m) => new RegExp(`(^|\\s)@${escaparRegex(m.username)}(?=$|[\\s.,;:!?])`).test(texto))
    .map((m) => m.id);
  return Array.from(new Set(ids));
}

function escaparRegex(texto: string): string {
  return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * O texto do campo com as menções ESCOLHIDAS destacadas, para a camada espelhada atrás do campo
 * (at-131). Só quem foi escolhido no autocomplete é destacado — o mesmo critério do envio.
 */
function textoDestacado(texto: string, mencoes: MencaoEscolhida[]): React.ReactNode[] {
  const nomes = Array.from(new Set(mencoes.map((m) => m.username)))
    .sort((a, b) => b.length - a.length)
    .map(escaparRegex);
  if (nomes.length === 0) return [texto];
  const padrao = new RegExp(`(^|\\s)(@(?:${nomes.join('|')}))(?=$|[\\s.,;:!?])`, 'g');
  const partes: React.ReactNode[] = [];
  let ultimo = 0;
  let achado: RegExpExecArray | null;
  // `exec` em laço, não `matchAll`: o alvo do CRA não garante o `lib` ES2020.
  while ((achado = padrao.exec(texto)) !== null) {
    const inicio = achado.index + achado[1].length;
    if (inicio > ultimo) partes.push(texto.slice(ultimo, inicio));
    partes.push(
      <span key={inicio} className="comentarios-mencao">
        {achado[2]}
      </span>,
    );
    ultimo = inicio + achado[2].length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
}

/**
 * O campo de texto dos comentários com autocomplete de menção (@) — at-129.
 *
 * Compartilhado pelos comentários do veículo e pelos de pedido/requisição: os dois formulários
 * (raiz e resposta) usam este campo, para a regra da menção ser uma só.
 *
 * 🔴 Três regras:
 * 1. **A menção vira ID na escolha**; o servidor nunca lê `@texto` do comentário.
 * 2. **Falha da busca não trava o campo**: a lista some com um aviso, e o comentário segue sem
 *    menção — a menção é acessório, e perder o texto por causa dela seria o pior desfecho.
 * 3. **`Escape` com a lista aberta fecha só a lista**: os modais que hospedam este campo fecham
 *    por `Escape`, e a tecla não pode derrubar o modal com o texto pela metade.
 */
const CampoComMencoes: React.FC<Props> = ({
  value,
  onChange,
  mencoes,
  onMencoesChange,
  placeholder,
  ariaLabel,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const espelhoRef = useRef<HTMLDivElement | null>(null);
  const [trecho, setTrecho] = useState<string | null>(null);
  const [sugestoes, setSugestoes] = useState<Sugestao[]>([]);
  const [ativa, setAtiva] = useState(0);

  useEffect(() => {
    if (trecho === null) {
      setSugestoes([]);
      return;
    }
    let cancelado = false;
    const id = setTimeout(() => {
      buscarMencionaveis(trecho)
        .then((lista) => {
          if (cancelado) return;
          setSugestoes(Array.isArray(lista) ? lista : []);
          setAtiva(0);
        })
        .catch(() => {
          if (cancelado) return;
          setSugestoes([]);
          // Polling-like: o campo pode disparar várias buscas seguidas — `toastId` evita a pilha.
          notify.warning('Não foi possível sugerir pessoas para mencionar. O comentário pode ser enviado mesmo assim.', {
            toastId: 'mencao-busca',
          });
        });
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(id);
    };
  }, [trecho]);

  const lerTrecho = (texto: string, cursor: number) => {
    const antes = texto.slice(0, cursor);
    const achado = TOKEN.exec(antes);
    setTrecho(achado ? achado[2] : null);
  };

  const escolher = (s: Sugestao) => {
    const el = ref.current;
    const cursor = el?.selectionStart ?? value.length;
    const antes = value.slice(0, cursor);
    const depois = value.slice(cursor);
    const nome = s.username || (s.fullName ?? '').replace(/\s+/g, '');
    if (!nome) return;
    const novoAntes = antes.replace(TOKEN, (_t, inicio: string) => `${inicio}@${nome} `);
    onChange(novoAntes + depois);
    if (!mencoes.some((m) => m.id === s.id)) onMencoesChange([...mencoes, { id: s.id, username: nome }]);
    setTrecho(null);
    setSugestoes([]);
    // Devolve o cursor ao ponto logo depois da menção inserida.
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(novoAntes.length, novoAntes.length);
    });
  };

  const aberta = sugestoes.length > 0;

  return (
    <div className="comentarios-mencao-campo">
      {/*
        at-131: a menção escolhida aparece azul e sublinhada JÁ NO CAMPO. Um <textarea> não aceita
        estilo em parte do texto, então o texto é desenhado nesta camada, atrás, e o campo fica
        transparente por cima (só o cursor e a seleção são dele). As duas precisam ter exatamente
        a mesma fonte, borda e espaçamento — por isso a camada usa a mesma classe do campo.
      */}
      <div ref={espelhoRef} className="comentarios-veiculo-textarea comentarios-mencao-espelho" aria-hidden="true">
        {textoDestacado(value, mencoes)}
        {/* Uma quebra final ocupa a linha do cursor quando o texto termina em Enter. */}
        {'\n'}
      </div>
      <textarea
        ref={ref}
        className="comentarios-veiculo-textarea comentarios-mencao-transparente"
        onScroll={(e) => {
          if (espelhoRef.current) espelhoRef.current.scrollTop = e.currentTarget.scrollTop;
        }}
        placeholder={placeholder}
        value={value}
        maxLength={10000}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          lerTrecho(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onKeyDown={(e) => {
          if (!aberta) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setAtiva((i) => (i + 1) % sugestoes.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setAtiva((i) => (i - 1 + sugestoes.length) % sugestoes.length);
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            escolher(sugestoes[ativa]);
          } else if (e.key === 'Escape') {
            // Fecha só a lista: o modal hospedeiro também escuta Escape.
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation?.();
            setTrecho(null);
            setSugestoes([]);
          }
        }}
      />
      {aberta && (
        <ul className="comentarios-mencao-lista" role="listbox" aria-label="Pessoas para mencionar">
          {sugestoes.map((s, i) => (
            <li
              key={s.id}
              role="option"
              aria-selected={i === ativa}
              className={`comentarios-mencao-item${i === ativa ? ' comentarios-mencao-ativa' : ''}`}
              // `mousedown` e não `click`: o clique tiraria o foco do campo antes de escolher.
              onMouseDown={(e) => {
                e.preventDefault();
                // at-131: não deixa o evento chegar ao `document`, onde os modais escutam o "clique
                // fora". Escolher remove a lista; quando o modal conferia o alvo, ele já não estava
                // dentro do modal — e o modal fechava, sem a pessoa conseguir mencionar ninguém.
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation?.();
                escolher(s);
              }}
            >
              {s.fullName || s.username}
              {s.username ? <span className="comentarios-mencao-usuario">@{s.username}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CampoComMencoes;
